import * as React from 'react'

import { SearchIcon } from 'lucide-react'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { RouteComponentProps, withRouter } from 'react-router'

import BottomNavigationNavBar from '~/client/src/mobile/components/BottomNavigationNavBar/BottomNavigationNavBar'
import MobileCommonStore from '~/client/src/mobile/stores/ui/MobileCommon.store'
import MobileBulkActionsBar from '~/client/src/mobile/views/Notifications/components/MobileBulkActionsBar'
import UnsafeNotificationItem from '~/client/src/mobile/views/Notifications/components/NotificationItem'
import CardList from '~/client/src/shared/components/CardList/CardList'
import CardListEmpty from '~/client/src/shared/components/CardList/CardListEmpty'
import DrillDownFilter from '~/client/src/shared/components/DrillDownFilter'
import { withErrorBoundary } from '~/client/src/shared/components/ErrorBoundary'
import FacetButton from '~/client/src/shared/components/Filters/components/FacetButton/FacetButton'
import { BicIcon } from '~/client/src/shared/components/icons/BicIcon'
import { UnreadIcon } from '~/client/src/shared/components/icons/UnreadIcon'
import {
  Content,
  Footer,
  Header,
  View,
} from '~/client/src/shared/components/Layout'
import SearchBar from '~/client/src/shared/components/SearchBar/SearchBar'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { clearSelection, setSearch } from '~/client/src/shared/lists/core'
import { buildNotificationsListFilterCategories } from '~/client/src/shared/lists/notifications/notificationListFilterCategories'
import type { INotificationRow } from '~/client/src/shared/lists/notifications/notificationSchema'
import {
  NotificationsListStore,
  NotificationTab,
} from '~/client/src/shared/lists/notifications/NotificationsList.store'
import { useListInteraction } from '~/client/src/shared/lists/useListInteraction'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import BaseNotification from '~/client/src/shared/models/Notification'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import NotificationsStore from '~/client/src/shared/stores/domain/Notifications.store'
import PermitTypesStore from '~/client/src/shared/stores/domain/PermitTypes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import {
  LOAD_NOTIFICATIONS,
  VIEW_NOTIFICATION,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import {
  getSearchParam,
  removeFromSearchParam,
} from '~/client/src/shared/utils/urlutils'

import MobileEventStore from '../../stores/EventStore/MobileEvents.store'

import './NotificationsList.scss'

const NotificationItem = withErrorBoundary(UnsafeNotificationItem)

interface IProps extends RouteComponentProps<any> {
  eventsStore?: MobileEventStore
  common?: MobileCommonStore
  notificationsStore?: NotificationsStore
  notificationsListStore?: NotificationsListStore
  interaction?: NotificationsListInteraction
  companiesStore?: CompaniesStore
  permitTypesStore?: PermitTypesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
}

type NotificationsListInteraction = ReturnType<
  typeof useListInteraction<INotificationRow>
>

@inject(
  'eventsStore',
  'common',
  'notificationsStore',
  'notificationsListStore',
  'companiesStore',
  'permitTypesStore',
  'projectMembersStore',
  'locationAttributesStore',
)
@observer
class NotificationsListComponent extends React.Component<IProps> {
  private store: NotificationsStore
  private scrollContainerRef: HTMLDivElement = null

  @observable private localProps: IProps = null
  @observable private showFilter: boolean = false
  @observable private isSearchFocused: boolean = false

  public constructor(props: IProps) {
    super(props)

    this.store = props.notificationsStore
    this.localProps = props

    makeObservable(this)
  }

  public componentWillUnmount() {
    // Save scroll position for restoration
    const scrollEl = this.getScrollElement()
    if (scrollEl) {
      this.store.savedScrollTop = scrollEl.scrollTop
    }
    this.store.markDisplayedAsRead()
    runInAction(() => {
      this.store.displayedNotification = null
    })
    this.props.notificationsListStore.list.dispatch(clearSelection())
  }

  public componentDidMount() {
    this.props.common.showNavBar()
    this.showNotificationByQueryString()

    // Restore scroll position after CardList renders
    requestAnimationFrame(() => {
      const scrollEl = this.getScrollElement()
      if (scrollEl && this.store.savedScrollTop) {
        scrollEl.scrollTop = this.store.savedScrollTop
      }
    })
  }

  private showNotificationByQueryString = () => {
    const VIEW_BY_ID = 'id'
    const notificationToDisplay = this.store.getLatestByEntityId(
      getSearchParam(VIEW_BY_ID),
    )
    if (notificationToDisplay) {
      removeFromSearchParam(VIEW_BY_ID, this.props.common.history)
      this.props.eventsStore.dispatch(VIEW_NOTIFICATION, notificationToDisplay)
    }
  }

  public componentDidUpdate(): void {
    if (getSearchParam('id')) {
      this.showNotificationByQueryString()
    }
  }

  @computed
  private get notificationRows(): readonly INotificationRow[] {
    return this.localProps.notificationsListStore.list.filteredRecords
  }

  @computed
  private get filteredNotifications(): BaseNotification[] {
    return this.notificationRows.map(row => row.notification)
  }

  public render() {
    const { interaction, notificationsListStore } = this.props

    return (
      <View>
        <Header className="col no-grow">
          {interaction.isSelectionMode ? (
            <MobileBulkActionsBar
              notificationsListStore={notificationsListStore}
              filteredNotifications={this.filteredNotifications}
              onExitMultiSelect={interaction.exitSelectionMode}
            />
          ) : (
            <>
              <div className="row space-between px8 bb-light-cool-grey mobile-notification-tabs">
                {this.renderPresetTabs()}
              </div>
              {/* Row 2: Search + filter icons */}
              <div className="row x-between y-center px8 py8 gap-4">
                <div className="row">
                  <SearchBar
                    value={notificationsListStore.list.query.search}
                    onChange={this.onSearchChange}
                    onReset={this.onSearchReset}
                    onTagClick={this.onSearchReset}
                    customPlaceholder={Localization.translator.search}
                    useAlternativeLayout
                    onFocus={this.onSearchFocus}
                    onBlur={this.onSearchBlur}
                    customSearchIcon={
                      <SearchIcon
                        size={16}
                        className="tw:mr-2 tw:text-muted-foreground"
                      />
                    }
                  />
                </div>
                <div className="row no-grow gap-4">
                  <FacetButton
                    type="filter"
                    isIconOnly
                    isSelected={this.showFilter}
                    isActive={this.activeFilterIds.length > 0}
                    onClick={this.toggleFilter}
                    showChevron={false}
                  />
                  {!this.isSearchFocused && (
                    <>
                      <FacetButton
                        type="filter"
                        icon={<BicIcon size={18} />}
                        label="BIC"
                        isActive={notificationsListStore.isBicFilterActive}
                        onClick={notificationsListStore.toggleBicOnly}
                        showChevron={false}
                      />
                      <FacetButton
                        type="filter"
                        icon={<UnreadIcon size="sm" />}
                        label={Localization.translator.unread}
                        isActive={notificationsListStore.isUnreadFilterActive}
                        onClick={notificationsListStore.toggleUnreadOnly}
                        showChevron={false}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="tw:text-sm tw:text-muted-foreground tw:px-2 tw:py-2.5 tw:border-b tw:border-border">
                {notificationsListStore.list.filteredCount}{' '}
                {Localization.translator.notifications}
              </div>
            </>
          )}
        </Header>
        <Content
          scrollable={false}
          className="notification-list"
          setRef={this.setScrollContainerRef}
        >
          <CardList
            items={[...this.notificationRows]}
            renderItem={this.renderNotification}
            loading={this.isLoading}
            loadingComponent={
              <div className="spinner-holder">
                <Spinner size="sm" />
              </div>
            }
            empty={
              <CardListEmpty
                message={Localization.translator.noDataAvailable}
              />
            }
          />
        </Content>
        <Footer>
          <BottomNavigationNavBar />
        </Footer>
        <DrillDownFilter.Drawer
          open={this.showFilter}
          onOpenChange={next => {
            if (!next) this.hideFilter()
          }}
        >
          <DrillDownFilter.Mobile
            categories={this.drillDownCategories}
            initialSelection={[...this.activeFilterIds]}
            onApply={this.handleFilterApply}
          />
        </DrillDownFilter.Drawer>
      </View>
    )
  }

  private renderPresetTabs() {
    const tabs = [
      { id: NotificationTab.All, label: Localization.translator.all },
      {
        id: NotificationTab.Important,
        label: Localization.translator.important,
      },
      { id: NotificationTab.Updates, label: Localization.translator.updates },
      { id: NotificationTab.Archive, label: Localization.translator.archive },
    ]
    return tabs.map(tab => (
      <div
        key={tab.id}
        className={classList({
          'notification-tab-item pointer py12 text large center': true,
          'selected-tab':
            this.props.notificationsListStore.activeTab === tab.id,
        })}
        onClick={() => this.props.notificationsListStore.setTab(tab.id)}
      >
        {tab.label}
      </div>
    ))
  }

  public renderNotification = (row: INotificationRow) => {
    return (
      <NotificationItem
        recordId={row.id}
        notification={row.notification}
        isMultiSelectMode={this.props.interaction.isSelectionMode}
        notificationsListStore={this.props.notificationsListStore}
        interactionHandlers={this.handlersForNotification(row.id)}
      />
    )
  }

  private get isLoading() {
    const { loading } = this.props.eventsStore.appState
    return loading.get(LOAD_NOTIFICATIONS)
  }

  @computed
  private get drillDownCategories() {
    return buildNotificationsListFilterCategories({
      listStore: this.localProps.notificationsListStore,
      companiesStore: this.localProps.companiesStore,
      permitTypesStore: this.localProps.permitTypesStore,
      projectMembersStore: this.localProps.projectMembersStore,
      locationAttributesStore: this.localProps.locationAttributesStore,
    })
  }

  @computed
  private get activeFilterIds(): readonly string[] {
    return this.localProps.notificationsListStore.prefixedFilterIds
  }

  @action.bound
  private toggleFilter() {
    this.showFilter = !this.showFilter
  }

  @action.bound
  private hideFilter() {
    this.showFilter = false
  }

  @action.bound
  private handleFilterApply(selectedIds: string[]) {
    if (selectedIds.length === 0) {
      this.props.notificationsListStore.setTab(NotificationTab.All, {
        preserveSearch: true,
      })
    } else {
      this.props.notificationsListStore.applyPrefixedFilterIds(selectedIds)
    }
  }

  @action.bound
  private onSearchChange(value: string) {
    this.props.notificationsListStore.list.dispatch(setSearch(value))
  }

  @action.bound
  private onSearchReset() {
    this.props.notificationsListStore.list.dispatch(setSearch(''))
  }

  private setScrollContainerRef = (el: HTMLDivElement) => {
    this.scrollContainerRef = el
  }

  private handlersForNotification(recordId: string) {
    const handlers = this.props.interaction.handlersFor(recordId)
    return {
      ...handlers,
      onClick: (e: React.MouseEvent): void => {
        if (this.props.interaction.isSelectionMode) {
          this.vibrateSelectionToggle()
        }
        handlers.onClick(e)
      },
    }
  }

  private vibrateSelectionToggle(): void {
    if (typeof navigator !== 'undefined') {
      navigator.vibrate?.(50)
    }
  }

  private getScrollElement(): HTMLElement | null {
    if (!this.scrollContainerRef) return null
    // CardList's scroll container has overflow: auto as inline style
    return (
      this.scrollContainerRef.querySelector<HTMLElement>(
        '[style*="overflow"]',
      ) || this.scrollContainerRef
    )
  }

  @action.bound
  private onSearchFocus() {
    this.isSearchFocused = true
  }

  @action.bound
  private onSearchBlur() {
    this.isSearchFocused = false
  }
}

// workaround for strange error in mobile routes
// see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/18999
const NotificationsListWithInteraction = inject(
  'eventsStore',
  'notificationsListStore',
)(
  observer((props: IProps) => {
    const { eventsStore, notificationsListStore } = props
    const activateNotification = React.useCallback(
      (recordId: string): void => {
        const row = notificationsListStore.list.filteredRecords.find(
          item => item.id === recordId,
        )
        if (!row) return

        eventsStore.dispatch(VIEW_NOTIFICATION, row.notification)
      },
      [eventsStore, notificationsListStore],
    )
    const interaction = useListInteraction(notificationsListStore.list, {
      holdToSelect: true,
      activateSelects: false,
      onActivate: activateNotification,
      scopeKey: eventsStore.appState.activeProject.id,
    })
    const wasSelectionMode = React.useRef(interaction.isSelectionMode)

    React.useEffect(() => {
      if (
        !wasSelectionMode.current &&
        interaction.isSelectionMode &&
        typeof navigator !== 'undefined' &&
        navigator.vibrate
      ) {
        navigator.vibrate(50)
      }
      wasSelectionMode.current = interaction.isSelectionMode
    }, [interaction.isSelectionMode])

    return <NotificationsListComponent {...props} interaction={interaction} />
  }),
)

export default withRouter(NotificationsListWithInteraction as any) as any
